import * as React from "react";

import MyLocationTwoTone from "@material-ui/icons/MyLocationTwoTone";
import { WithTranslation, withTranslation } from "react-i18next";
import Topic from "~/components/CustomerSupport/Topic";
import BanggoodShipmentDetail from "~/components/TrackTrace/BanggoodShipmentDetail";
import Loader from "~/components/TrackTrace/Loader";
import ShipmentDeadlineException from "~/components/TrackTrace/ShipmentDeadlineException";
import ShipmentProgressbar from "~/components/TrackTrace/ShipmentProgressbar";
import { ShipmentState } from "~/components/TrackTrace/types";
import { IOrder } from "~/graphql/queries/order/orderByNumberAndPostalCode";
import {
  IShipment,
  ShipmentStatus
} from "~/graphql/queries/shipment/shipmentsByIds";
import { Supplier } from "~/graphql/types";
import { shipmentIdsFromOrder } from "~/helpers/shipmentIdsFromOrder";
import SHIPMENT_STATE_DESCRIPTIONS from "~/helpers/shipmentStateDescriptions";
import { StyledCard as Card } from "~/layouts/StyledCard";
import TrackNowLink from "./TrackNowLink";

interface IProps {
  order: IOrder;
  shipments: IShipment[];
}

const differenceInBusinessDays = (startDate: Date, endDate: Date) => {
  let workDays = 0;
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    if (currentDate.getDay() % 6 > 0) {
      workDays++;
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return workDays;
};

class ShipmentDetail extends React.Component<IProps & WithTranslation> {
  public render() {
    const { t, order, shipments } = this.props;

    const maybeBanggoodOrder = order.orderItems.find(
      orderItem => orderItem.supplier === Supplier.BANGGOOD
    );

    if (!shipments) {
      return <Loader />;
    }

    const shipment = shipments[0];
    const shipmentState = shipment
      ? this.mapShipmentStatus(shipment)
      : ShipmentState.PROCESSING;
    const Description = SHIPMENT_STATE_DESCRIPTIONS[shipmentState];

    // Check if the shipments are within deadlines
    const withTrackAndTrace = order.shipmentPrice > 0;
    const shipmentDeadline = withTrackAndTrace ? 15 : 25;

    const businessDaysSinceOrder = differenceInBusinessDays(
      order.createdAt,
      new Date()
    );

    // If the shipment deadline exceeded, we're going to do additional checks
    if (
      shipmentState !== ShipmentState.DELIVERY &&
      businessDaysSinceOrder > shipmentDeadline
    ) {
      // If we have shipment data, we're going to check if we're still receiving updates
      if (shipment) {
        const lastShippingEventAt = shipment.events.reduce(
          (acc, event) => (event.createdAt > acc ? event.createdAt : acc),
          order.createdAt
        );
        const businessDaysSinceLastShipmentEvent = differenceInBusinessDays(
          lastShippingEventAt,
          new Date()
        );

        // Only if the last update was long ago, we show the exception
        if (businessDaysSinceLastShipmentEvent > 5) {
          return (
            <ShipmentDeadlineException
              trackAndTrace={withTrackAndTrace}
              orderNumber={order.number}
            />
          );
        }
      } else {
        // If by now, we still don't have info about the shipment, we show the exception as well
        return (
          <ShipmentDeadlineException
            trackAndTrace={withTrackAndTrace}
            orderNumber={order.number}
          />
        );
      }
    }

    // Banggood orders cannot be tracked with our current API so we show a simple button to 17Track
    if (maybeBanggoodOrder && !shipment) {
      return <BanggoodShipmentDetail order={order} />;
    }

    const trackingIds = shipmentIdsFromOrder(order).join(",");

    return (
      <>
        <Card>
          <Topic
            icon={MyLocationTwoTone}
            title={t("title")}
            description={<Description order={order} shipment={shipment} />}
          />

          <ShipmentProgressbar state={shipmentState} />
        </Card>
        {shipment && shipment.id && <TrackNowLink trackingIds={trackingIds} />}
      </>
    );
  }

  private mapShipmentStatus(shipment: IShipment): ShipmentState {
    if (!shipment.events.length) {
      return ShipmentState.PROCESSING;
    }

    const KNOWN_STATUSES = [
      ShipmentStatus.ARRIVED_DESTINATION_COUNTRY,
      ShipmentStatus.DEPARTED_ORIGIN_COUNTRY,
      ShipmentStatus.DELIVERED,
      ShipmentStatus.IN_TRANSIT,
      ShipmentStatus.PICKED_UP_BY_CARRIER
    ];

    const lastStatusEvent = shipment.events.find(
      event => (event.status && KNOWN_STATUSES.includes(event.status)) || false
    );

    if (!lastStatusEvent) {
      return ShipmentState.PROCESSING;
    }

    switch (lastStatusEvent.status) {
      case ShipmentStatus.DEPARTED_ORIGIN_COUNTRY:
        return ShipmentState.DEPART_ORIGIN_COUNTRY;
      case ShipmentStatus.ARRIVED_DESTINATION_COUNTRY:
        return ShipmentState.ARRIVE_DESTINATION_COUNTRY;
      case ShipmentStatus.DELIVERED:
        return ShipmentState.DELIVERY;
    }

    return ShipmentState.DEPART_ORIGIN_COUNTRY;
  }
}

export default withTranslation("trackTrace")(ShipmentDetail);



// WEBPACK FOOTER //
// ./src/components/TrackTrace/ShipmentDetail.tsx